/**
 * schemas/cash/assignment.ts
 * ──────────────────────────────────────────────────────────────────────────
 * An assignment is money placed in one holder's custody — one row in the
 * register, one drawer, one balance.
 *
 * Terminology note: this entity was called a "float" internally and still is
 * in some API identifiers. The product word is ASSIGNMENT everywhere a person
 * can see it, and that mapping is not repeated per screen — the field names
 * here are the API's, the labels come from lib/status.ts.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { currency, isoDate, isoDateNullable, minor, minorNullable } from '../common';

/** Fixed enum — never a configurable option list. Each value changes behaviour. */
export const SCOPES = ['open', 'purpose', 'collection'] as const;
export type Scope = (typeof SCOPES)[number];

/** What happens to unspent cash when a purpose assignment closes. */
export const END_RULES = ['return_to_company', 'release_to_holder'] as const;
export type EndRule = (typeof END_RULES)[number];

export const ASSIGNMENT_STATUSES = ['active', 'suspended', 'closed'] as const;

export const assignmentSchema = z.object({
  id: z.string(),
  /** Display name of whoever holds the money. */
  holder: z.string(),
  /** From the org's holderType option list — `employee`, `vehicle`, … */
  holder_type: z.string(),
  /**
   * The opaque id into People/Platform. For a person this IS their
   * platform_user_id, which is what makes the custodian's "only my own cash"
   * check meaningful rather than a naming coincidence.
   */
  holder_ref: z.string(),
  scope: z.string(),
  instrument: z.string(),
  funding: z.string(),
  cur: currency,
  /** Opaque region from the owning vertical. Free text — only it knows. */
  jurisdiction: z.string().nullable(),
  /** The target a limit count is measured against. Null on collection scope. */
  limit_minor: minorNullable,
  balance_minor: minor,
  status: z.string(),
  purpose_label: z.string().nullable(),
  purpose_expires_at: isoDateNullable,
  end_rule: z.string().nullable(),
  opened_at: isoDate,
  last_recon_at: isoDateNullable,
  /** Every balance states its as-of time. A position without one is a claim. */
  balance_as_of: isoDateNullable,
  /** Computed server-side against the org's own staleDays. */
  stale: z.boolean(),
});
export type Assignment = z.infer<typeof assignmentSchema>;

export const assignmentsResponseSchema = z.object({ assignments: z.array(assignmentSchema) });

/** POST /v1/cash/assignments — the "Assign cash" modal. */
export const assignmentCreateSchema = z.object({
  holder: z.string().min(1).max(160),
  holder_type: z.string().min(1).max(60),
  holder_ref: z.string().min(1).max(200),
  scope: z.enum(SCOPES),
  instrument: z.string().min(1).max(60),
  funding: z.string().min(1).max(60),
  cur: currency,
  jurisdiction: z.string().max(60).optional(),
  limit_minor: z.string().optional(),
  card_provider_ref: z.string().max(200).optional(),
  // purpose scope only — the modal reveals these when `purpose` is chosen
  purpose_label: z.string().max(160).optional(),
  purpose_cat_id: z.string().optional(),
  purpose_expires_at: z.string().optional(),
  end_rule: z.enum(END_RULES).optional(),
});
export type AssignmentCreate = z.infer<typeof assignmentCreateSchema>;

/** POST /v1/cash/assignments/:id/transfer — hand custody to someone else. */
export const transferSchema = z.object({
  to_holder: z.string().min(1).max(160),
  to_type: z.string().min(1).max(60),
  to_ref: z.string().min(1).max(200),
  reason: z.string().max(160).optional(),
});
export type TransferInput = z.infer<typeof transferSchema>;

export const transferRowSchema = z.object({
  id: z.string(),
  from_holder: z.string().nullable().optional(),
  to_holder: z.string().nullable().optional(),
  balance_minor: minorNullable.optional(),
  cur: currency.optional(),
  reason: z.string().nullable().optional(),
  created_at: isoDate.optional(),
});
